import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, Ban, Eraser, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { optionalBio } from "../lib/passkey";

const monthAr = (ym) => {
  if (!ym) return "";
  const M = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
  const [y, m] = ym.split("-");
  return `${M[parseInt(m, 10) - 1] || m} ${y}`;
};

// Blocks portal access until the rider acknowledges a target-failure warning (or shows suspension).
export const TargetWarningGate = ({ onCleared }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasSig = useRef(false);

  useEffect(() => {
    api.portalTargetStatus()
      .then((d) => { setData(d); if (d.status === "ok") onCleared?.(); })
      .catch(() => { setData({ status: "ok" }); onCleared?.(); })
      .finally(() => setLoading(false));
  }, [onCleared]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* noop */ } const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(p.x, p.y); ctx.stroke(); hasSig.current = true; };
  const end = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasSig.current = false; };

  const submit = useCallback(async () => {
    if (!justification.trim()) { toast.error("الرجاء كتابة المبرر"); return; }
    if (!hasSig.current) { toast.error("الرجاء التوقيع في المربع"); return; }
    setSubmitting(true);
    try {
      const sig = canvasRef.current.toDataURL("image/png");
      const res = await api.portalTargetAck(justification.trim(), sig, await optionalBio("target_ack"));
      if (res.status === "suspended") { setData({ status: "suspended", texts: data?.texts }); return; }
      toast.success("تم حفظ إقرارك");
      setData({ status: "ok" });
      onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحفظ");
    } finally { setSubmitting(false); }
  }, [justification, onCleared]);

  if (loading) return <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
  if (!data || data.status === "ok") return null;

  if (data.status === "suspended") {
    return (
      <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-900/95 p-5" dir="rtl" data-testid="target-suspended">
        <div className="max-w-md w-full rounded-3xl bg-white p-7 text-center space-y-4 border-4 border-red-500">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-100"><Ban className="h-9 w-9 text-red-600" /></div>
          <h2 className="font-heading text-xl font-extrabold text-red-700">نأسف عن إيقافك عن العمل</h2>
          <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{data.texts?.suspend || "تم إيقافك مؤقتاً بسبب عدم تحقيق التارقت المطلوب لمدة 3 أشهر متتالية. الرجاء مراجعة الإدارة للتحقيق."}</p>
          <button onClick={() => { api.portalLogout?.(); window.location.reload(); }} data-testid="suspended-logout"
            className="w-full rounded-xl bg-slate-800 py-2.5 font-semibold text-white hover:bg-slate-900">تسجيل الخروج</button>
        </div>
      </div>
    );
  }

  // warning
  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-900/95 p-4" dir="rtl" data-testid="target-warning-gate">
      <div className="mx-auto my-6 max-w-lg rounded-3xl bg-white p-6 space-y-5 border-4 border-amber-400">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100"><AlertTriangle className="h-9 w-9 text-amber-600" /></div>
          <h2 className="font-heading text-xl font-extrabold text-slate-900">{data.texts?.title || "إنذار بعدم تحقيق التارقت"}</h2>
          <span className="inline-block rounded-full bg-red-100 text-red-700 px-3 py-1 text-sm font-bold">
            الإنذار رقم ({data.warning_number}){data.warning_number === 2 ? " والأخير" : ""}
          </span>
        </div>
        {data.warning_number === 2 && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-center" data-testid="target-final-warning-note">
            <p className="text-red-700 font-bold leading-relaxed whitespace-pre-wrap">
              {data.texts?.final || "⚠️ هذا هو الإنذار الثاني والأخير. في حال تكرار عدم تحقيق التارقت (الإنذار الثالث) سيتم إنهاء العقد مباشرةً استناداً إلى المادة 80 من نظام العمل."}
            </p>
          </div>
        )}
        <p className="text-slate-700 leading-relaxed text-center whitespace-pre-wrap">
          {(data.texts?.body || "بطلنا العزيز، للأسف لم تحقّق التارقت المطلوب منك عن شهر {month}.").replace("{month}", monthAr(data.month))}
        </p>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-400">طلباتك</div><div className="font-heading text-2xl font-extrabold font-mono-nums text-slate-800">{data.orders}</div></div>
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-400">الهدف المطلوب</div><div className="font-heading text-2xl font-extrabold font-mono-nums text-blue-600">{data.target}</div></div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">المبرّرات (إجباري)</label>
          <textarea data-testid="target-justification-input" value={justification} onChange={(e) => setJustification(e.target.value)} rows={3}
            placeholder="اكتب أسباب عدم تحقيق التارقت..." className="w-full rounded-xl border border-slate-300 p-3 text-sm focus:ring-2 focus:ring-amber-400 outline-none" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-slate-700">التوقيع (إقرار بصحة البيانات)</label>
            <button type="button" onClick={clearSig} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700"><Eraser className="h-3.5 w-3.5" /> مسح</button>
          </div>
          <canvas ref={canvasRef} width={520} height={160} data-testid="target-signature-canvas"
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
            style={{ touchAction: "none" }}
            className="w-full h-40 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair" />
        </div>
        <button onClick={submit} disabled={submitting} data-testid="target-ack-submit"
          className="w-full rounded-xl bg-amber-500 py-3 font-bold text-white hover:bg-amber-600 disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />} أقرّ بصحة البيانات وأتعهّد بالتحسّن
        </button>
      </div>
    </div>
  );
};
